/**
 * AnalyticsDashboard component for viewing pageview analytics.
 *
 * Features:
 * - Overview stats (total views, unique visitors, avg duration)
 * - Views timeseries chart (CSS-based bar chart)
 * - Top pages and essays tables
 * - Referrer breakdown
 * - Device/browser breakdown
 * - Recent pageviews live-tail
 * - Date range picker
 *
 * Called by:
 * - AdminPage.tsx Analytics tab
 */
import { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Space,
  Typography,
  Spin,
  Empty,
  DatePicker,
  Segmented,
  Progress,
  Tooltip,
} from 'antd';
import {
  EyeOutlined,
  UserOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  LinkOutlined,
  DesktopOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  getAnalyticsOverview,
  getAnalyticsPages,
  getAnalyticsReferrers,
  getAnalyticsTimeseries,
  getAnalyticsEssays,
  getAnalyticsDevices,
  getAnalyticsRecent,
} from '../../services/api';
import type {
  AnalyticsPage,
  AnalyticsEssay,
} from '../../services/api';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

type RangePreset = '7d' | '30d' | '90d' | 'custom';

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

// =============================================================================
// Mini Bar Chart (CSS-based, no chart library needed)
// =============================================================================

function MiniBarChart({ data }: { data: { date: string; views: number; uniqueVisitors: number }[] }) {
  const maxViews = Math.max(...data.map(d => d.views), 1);

  if (data.length === 0) {
    return <Empty description="No data for this period" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '160px', padding: '0 4px' }}>
      {data.map((d) => {
        const height = Math.max((d.views / maxViews) * 140, 2);
        return (
          <Tooltip
            key={d.date}
            title={
              <div>
                <div>{dayjs(d.date).format('MMM D, YYYY')}</div>
                <div>{d.views} views</div>
                <div>{d.uniqueVisitors} unique</div>
              </div>
            }
          >
            <div
              style={{
                flex: 1,
                minWidth: '4px',
                maxWidth: '24px',
                height: `${height}px`,
                background: 'linear-gradient(180deg, #d4af37 0%, #d4af3788 100%)',
                borderRadius: '2px 2px 0 0',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.opacity = '0.8'; }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.opacity = '1'; }}
            />
          </Tooltip>
        );
      })}
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

function AnalyticsDashboard() {
  const [rangePreset, setRangePreset] = useState<RangePreset>('30d');
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const { from, to } = useMemo(() => {
    if (rangePreset === 'custom' && customRange) {
      return {
        from: customRange[0].format('YYYY-MM-DD'),
        to: customRange[1].format('YYYY-MM-DD'),
      };
    }
    const days = rangePreset === '7d' ? 7 : rangePreset === '90d' ? 90 : 30;
    return {
      from: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
    };
  }, [rangePreset, customRange]);

  // Queries
  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ['analyticsOverview', from, to],
    queryFn: () => getAnalyticsOverview(from, to),
    refetchInterval: 60000,
  });

  const { data: timeseries, isLoading: loadingTimeseries } = useQuery({
    queryKey: ['analyticsTimeseries', from, to],
    queryFn: () => getAnalyticsTimeseries(from, to),
  });

  const { data: pages } = useQuery({
    queryKey: ['analyticsPages', from, to],
    queryFn: () => getAnalyticsPages(from, to),
  });

  const { data: essays } = useQuery({
    queryKey: ['analyticsEssays', from, to],
    queryFn: () => getAnalyticsEssays(from, to),
  });

  const { data: referrers } = useQuery({
    queryKey: ['analyticsReferrers', from, to],
    queryFn: () => getAnalyticsReferrers(from, to),
  });

  const { data: devices } = useQuery({
    queryKey: ['analyticsDevices', from, to],
    queryFn: () => getAnalyticsDevices(from, to),
  });

  const { data: recent } = useQuery({
    queryKey: ['analyticsRecent'],
    queryFn: () => getAnalyticsRecent(100),
    refetchInterval: 30000,
  });

  // Loading
  if (loadingOverview && loadingTimeseries) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Column definitions
  const pageColumns = [
    {
      title: 'Page',
      dataIndex: 'path',
      key: 'path',
      render: (path: string, record: AnalyticsPage) => (
        <div>
          <Text style={{ color: '#f5f5f5' }}>{path}</Text>
          {record.essaySlug && (
            <div><Text type="secondary" style={{ fontSize: '12px' }}>{record.essaySlug}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      width: 100,
      sorter: (a: AnalyticsPage, b: AnalyticsPage) => a.views - b.views,
      render: (v: number) => <Text style={{ color: '#f5f5f5' }}>{v.toLocaleString()}</Text>,
    },
    {
      title: 'Unique',
      dataIndex: 'uniqueVisitors',
      key: 'uniqueVisitors',
      width: 100,
      render: (v: number) => <Text type="secondary">{v.toLocaleString()}</Text>,
    },
    {
      title: 'Avg Duration',
      dataIndex: 'avgDuration',
      key: 'avgDuration',
      width: 120,
      render: (v: number) => <Text type="secondary">{formatDuration(v)}</Text>,
    },
  ];

  const essayColumns = [
    {
      title: 'Essay',
      dataIndex: 'essaySlug',
      key: 'essaySlug',
      render: (slug: string) => <Text style={{ color: '#f5f5f5' }}>{slug}</Text>,
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      width: 100,
      sorter: (a: AnalyticsEssay, b: AnalyticsEssay) => a.views - b.views,
      render: (v: number) => <Text style={{ color: '#f5f5f5' }}>{v.toLocaleString()}</Text>,
    },
    {
      title: 'Unique',
      dataIndex: 'uniqueVisitors',
      key: 'uniqueVisitors',
      width: 100,
      render: (v: number) => <Text type="secondary">{v.toLocaleString()}</Text>,
    },
    {
      title: 'Avg Duration',
      dataIndex: 'avgDuration',
      key: 'avgDuration',
      width: 120,
      render: (v: number) => <Text type="secondary">{formatDuration(v)}</Text>,
    },
  ];

  const referrerColumns = [
    {
      title: 'Referrer',
      dataIndex: 'referrer',
      key: 'referrer',
      render: (r: string) => (
        <Text style={{ color: '#f5f5f5' }}>{r || <Text type="secondary">Direct</Text>}</Text>
      ),
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      width: 100,
      render: (v: number) => <Text style={{ color: '#f5f5f5' }}>{v.toLocaleString()}</Text>,
    },
    {
      title: 'Unique',
      dataIndex: 'uniqueVisitors',
      key: 'uniqueVisitors',
      width: 100,
      render: (v: number) => <Text type="secondary">{v.toLocaleString()}</Text>,
    },
  ];

  const recentColumns = [
    {
      title: 'Page',
      dataIndex: 'path',
      key: 'path',
      render: (path: string) => <Text style={{ color: '#f5f5f5', fontSize: '13px' }}>{path}</Text>,
    },
    {
      title: 'Referrer',
      dataIndex: 'referrer',
      key: 'referrer',
      width: 200,
      render: (r: string | null) => (
        <Text type="secondary" style={{ fontSize: '13px' }} ellipsis>
          {r || 'Direct'}
        </Text>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 90,
      render: (d: number | null) => (
        <Text type="secondary" style={{ fontSize: '13px' }}>
          {d != null ? formatDuration(d) : '-'}
        </Text>
      ),
    },
    {
      title: 'When',
      dataIndex: 'viewedAt',
      key: 'viewedAt',
      width: 140,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: '13px' }}>
          {dayjs(v).fromNow()}
        </Text>
      ),
    },
  ];

  return (
    <div>
      {/* Date range controls */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Segmented
          value={rangePreset}
          onChange={(val) => setRangePreset(val as RangePreset)}
          options={[
            { label: '7 days', value: '7d' },
            { label: '30 days', value: '30d' },
            { label: '90 days', value: '90d' },
            { label: 'Custom', value: 'custom' },
          ]}
        />
        {rangePreset === 'custom' && (
          <RangePicker
            value={customRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setCustomRange([dates[0], dates[1]]);
              }
            }}
            style={{ background: '#1a1a1a' }}
          />
        )}
        <Text type="secondary" style={{ marginLeft: 'auto' }}>
          {dayjs(from).format('MMM D')} - {dayjs(to).format('MMM D, YYYY')}
        </Text>
      </div>

      {/* Overview stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Space><EyeOutlined style={{ color: '#d4af37' }} /><span>Total Views</span></Space>}
              value={overview?.totalViews || 0}
              valueStyle={{ color: '#f5f5f5' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Space><UserOutlined style={{ color: '#52c41a' }} /><span>Unique Visitors</span></Space>}
              value={overview?.uniqueVisitors || 0}
              valueStyle={{ color: '#f5f5f5' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Space><ClockCircleOutlined style={{ color: '#faad14' }} /><span>Avg Duration</span></Space>}
              value={formatDuration(overview?.avgDuration || 0)}
              valueStyle={{ color: '#f5f5f5' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Timeseries chart */}
      <Card
        title={<Space><EyeOutlined /><span>Views Over Time</span></Space>}
        style={{ marginTop: '16px', background: '#1a1a1a', borderColor: '#333' }}
      >
        {timeseries && timeseries.length > 0 ? (
          <>
            <MiniBarChart data={timeseries} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', padding: '0 4px' }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {dayjs(timeseries[0]!.date).format('MMM D')}
              </Text>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {dayjs(timeseries[timeseries.length - 1]!.date).format('MMM D')}
              </Text>
            </div>
          </>
        ) : (
          <Empty description="No views in this period" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      {/* Essays + Pages tables side by side */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col xs={24} lg={12}>
          <Card
            title={<Space><FileTextOutlined /><span>Top Essays</span></Space>}
            style={{ background: '#1a1a1a', borderColor: '#333' }}
          >
            <Table
              dataSource={essays || []}
              columns={essayColumns}
              rowKey="essaySlug"
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty description="No essay views" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<Space><GlobalOutlined /><span>Top Pages</span></Space>}
            style={{ background: '#1a1a1a', borderColor: '#333' }}
          >
            <Table
              dataSource={pages || []}
              columns={pageColumns}
              rowKey="path"
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty description="No page views" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Card>
        </Col>
      </Row>

      {/* Referrers + Devices side by side */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col xs={24} lg={12}>
          <Card
            title={<Space><LinkOutlined /><span>Top Referrers</span></Space>}
            style={{ background: '#1a1a1a', borderColor: '#333' }}
          >
            <Table
              dataSource={referrers || []}
              columns={referrerColumns}
              rowKey="referrer"
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty description="No referrer data" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<Space><DesktopOutlined /><span>Devices & Browsers</span></Space>}
            style={{ background: '#1a1a1a', borderColor: '#333' }}
          >
            {devices ? (
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Title level={5} style={{ marginBottom: '12px', color: '#f5f5f5' }}>Devices</Title>
                  {devices.devices.map((d) => {
                    const total = devices.devices.reduce((s, x) => s + x.views, 0) || 1;
                    const pct = Math.round((d.views / total) * 100);
                    return (
                      <div key={d.name} style={{ marginBottom: '8px' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Text style={{ color: '#f5f5f5' }}>{d.name}</Text>
                          <Text type="secondary">{pct}%</Text>
                        </Space>
                        <Progress
                          percent={pct}
                          showInfo={false}
                          strokeColor="#d4af37"
                          trailColor="#333"
                          size="small"
                        />
                      </div>
                    );
                  })}
                </Col>
                <Col span={12}>
                  <Title level={5} style={{ marginBottom: '12px', color: '#f5f5f5' }}>Browsers</Title>
                  {devices.browsers.map((b) => {
                    const total = devices.browsers.reduce((s, x) => s + x.views, 0) || 1;
                    const pct = Math.round((b.views / total) * 100);
                    return (
                      <div key={b.name} style={{ marginBottom: '8px' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Text style={{ color: '#f5f5f5' }}>{b.name}</Text>
                          <Text type="secondary">{pct}%</Text>
                        </Space>
                        <Progress
                          percent={pct}
                          showInfo={false}
                          strokeColor="#52c41a"
                          trailColor="#333"
                          size="small"
                        />
                      </div>
                    );
                  })}
                </Col>
              </Row>
            ) : (
              <Empty description="No device data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent views */}
      <Card
        title={<Space><UnorderedListOutlined /><span>Recent Views</span></Space>}
        style={{ marginTop: '16px', background: '#1a1a1a', borderColor: '#333' }}
        extra={<Tag color="gold">Live</Tag>}
      >
        <Table
          dataSource={recent || []}
          columns={recentColumns}
          rowKey="id"
          pagination={{ pageSize: 20, size: 'small', showSizeChanger: false }}
          size="small"
          locale={{ emptyText: <Empty description="No recent views" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </Card>
    </div>
  );
}

export default AnalyticsDashboard;
